/**
 * Contact quality ESTIMATED FROM THE SIGNAL: the classic 4-channel BrainBit's
 * FALLBACK when its measured resistance readout (`classicResistance.ts`, R5)
 * fails, times out or is switched off — and, before R5, its only readout
 * (Sprint 1.9.7 handoff §1.4). Unchanged by R5.
 *
 * The vendor's own advice is that amplitude and shape beat resistance as a
 * check of contact. Observed on a classic unit (2026-09-22): raw DC offsets
 * −53…+87 mV, a lifted contact pinned at +0.400 V; after settling, σ 7–13 µV
 * on all four channels after a 1–40 Hz filter.
 *
 * Rules (PROVISIONAL, decided for this sprint and marked so on the page):
 *   rail      |raw| > RAIL_UV                  → No contact (pinned at the ADC rail)
 *   flat      σ(filtered) < FLAT_UV            → No contact (nothing coupled)
 *   quiet     FLAT_UV ≤ σ < SEATED_MIN_UV       → Weak (too little for skin)
 *   seated    SEATED_MIN_UV ≤ σ < SEATED_MAX_UV → Good
 *   noisy     SEATED_MAX_UV ≤ σ < USABLE_MAX_UV → Usable
 *   rough     USABLE_MAX_UV ≤ σ < WEAK_MAX_UV   → Weak
 *   swinging  σ ≥ WEAK_MAX_UV                   → No contact (moving or lifted)
 *
 * The estimate is reported as a TIER, mapped onto the tier's representative kΩ
 * (`quality.REPRESENTATIVE_KOHM`) so the phase machine, the head map and the
 * meter need no second vocabulary. The screen says "estimated from the signal".
 */

import { REPRESENTATIVE_KOHM, type Tier } from '../capture/quality'

export const RAIL_UV = 390_000 // the ±0.4 V rail, with a little margin
export const FLAT_UV = 1.0
export const SEATED_MIN_UV = 5.0
export const SEATED_MAX_UV = 30.0
export const USABLE_MAX_UV = 80.0
export const WEAK_MAX_UV = 200.0
export const WINDOW_SECONDS = 1.0

/** A direct-form biquad; coefficients from the RBJ cookbook. */
export class Biquad {
  private b0 = 1
  private b1 = 0
  private b2 = 0
  private a1 = 0
  private a2 = 0
  private z1 = 0
  private z2 = 0

  static highpass(fs: number, fc: number, q = Math.SQRT1_2): Biquad {
    const f = new Biquad()
    const w0 = (2 * Math.PI * fc) / fs
    const alpha = Math.sin(w0) / (2 * q)
    const cos = Math.cos(w0)
    const a0 = 1 + alpha
    f.b0 = (1 + cos) / 2 / a0
    f.b1 = -(1 + cos) / a0
    f.b2 = (1 + cos) / 2 / a0
    f.a1 = (-2 * cos) / a0
    f.a2 = (1 - alpha) / a0
    return f
  }

  static lowpass(fs: number, fc: number, q = Math.SQRT1_2): Biquad {
    const f = new Biquad()
    const w0 = (2 * Math.PI * fc) / fs
    const alpha = Math.sin(w0) / (2 * q)
    const cos = Math.cos(w0)
    const a0 = 1 + alpha
    f.b0 = (1 - cos) / 2 / a0
    f.b1 = (1 - cos) / a0
    f.b2 = (1 - cos) / 2 / a0
    f.a1 = (-2 * cos) / a0
    f.a2 = (1 - alpha) / a0
    return f
  }

  step(x: number): number {
    // Transposed direct form II.
    const y = this.b0 * x + this.z1
    this.z1 = this.b1 * x - this.a1 * y + this.z2
    this.z2 = this.b2 * x - this.a2 * y
    return y
  }

  reset(): void {
    this.z1 = 0
    this.z2 = 0
  }
}

/** 1–40 Hz display/quality bandpass: one high-pass and one low-pass biquad. */
export class Bandpass {
  private readonly hp: Biquad
  private readonly lp: Biquad
  constructor(fs: number, lo = 1, hi = 40) {
    this.hp = Biquad.highpass(fs, lo)
    this.lp = Biquad.lowpass(fs, hi)
  }
  step(x: number): number {
    return this.lp.step(this.hp.step(x))
  }
  reset(): void {
    this.hp.reset()
    this.lp.reset()
  }
}

export function tierForSigma(sigma: number, railed: boolean): Tier {
  if (railed) return 'none'
  if (!Number.isFinite(sigma) || sigma < FLAT_UV) return 'none'
  if (sigma < SEATED_MIN_UV) return 'weak'
  if (sigma < SEATED_MAX_UV) return 'good'
  if (sigma < USABLE_MAX_UV) return 'ok'
  if (sigma < WEAK_MAX_UV) return 'weak'
  return 'none'
}

/** Per-channel running estimate over the last WINDOW_SECONDS of filtered signal. */
export class ChannelEstimator {
  private readonly filter: Bandpass
  private readonly buf: Float64Array
  private readonly rawBuf: Float64Array
  private idx = 0
  private filled = 0
  /** Samples seen since reset; the filter needs a moment to settle. */
  private seen = 0
  private readonly warmup: number
  readonly fs: number

  constructor(fs: number) {
    this.fs = fs
    this.filter = new Bandpass(fs)
    const n = Math.max(8, Math.round(fs * WINDOW_SECONDS))
    this.buf = new Float64Array(n)
    this.rawBuf = new Float64Array(n)
    this.warmup = Math.round(fs * 0.5)
  }

  push(raw: number): void {
    const y = this.filter.step(raw)
    this.buf[this.idx] = y
    this.rawBuf[this.idx] = raw
    this.idx = (this.idx + 1) % this.buf.length
    if (this.filled < this.buf.length) this.filled++
    this.seen++
  }

  /** null until enough samples have arrived to say anything. */
  estimate(): { sigma: number; railed: boolean; tier: Tier } | null {
    if (this.filled < this.buf.length || this.seen < this.warmup + this.buf.length) return null
    let railed = false
    let sum = 0
    for (let i = 0; i < this.filled; i++) {
      const r = this.rawBuf[i]!
      if (Math.abs(r) > RAIL_UV) railed = true
      sum += this.buf[i]!
    }
    const mean = sum / this.filled
    let ss = 0
    for (let i = 0; i < this.filled; i++) {
      const d = this.buf[i]! - mean
      ss += d * d
    }
    const sigma = Math.sqrt(ss / this.filled)
    return { sigma, railed, tier: tierForSigma(sigma, railed) }
  }

  reset(): void {
    this.filter.reset()
    this.idx = 0
    this.filled = 0
    this.seen = 0
  }
}

/** Estimates for a whole headband: feed samples, read kΩ-shaped readings. */
export class SignalQualityEstimator {
  private readonly per: Map<string, ChannelEstimator>
  readonly channels: readonly string[]

  constructor(channels: readonly string[], fs: number) {
    this.channels = channels
    this.per = new Map(channels.map((ch) => [ch, new ChannelEstimator(fs)]))
  }

  push(values: readonly number[]): void {
    this.channels.forEach((ch, i) => this.per.get(ch)!.push(values[i] ?? 0))
  }

  /**
   * Readings in the phase machine's vocabulary: the representative kΩ of the
   * estimated tier, or `null` while there is not yet enough signal to say.
   */
  readings(): Record<string, number | null> {
    const out: Record<string, number | null> = {}
    for (const ch of this.channels) {
      const e = this.per.get(ch)!.estimate()
      out[ch] = e === null ? null : REPRESENTATIVE_KOHM[e.tier]
    }
    return out
  }

  /** The raw estimate per channel, for the operator readout. */
  detail(): Record<string, { sigma: number; railed: boolean; tier: Tier } | null> {
    const out: Record<string, { sigma: number; railed: boolean; tier: Tier } | null> = {}
    for (const ch of this.channels) out[ch] = this.per.get(ch)!.estimate()
    return out
  }

  reset(): void {
    for (const e of this.per.values()) e.reset()
  }
}
